import type { OrderVariant, PriceTier, Product, ProductFaq } from '@/types/products';

/**
 * Formato de los datos del catálogo para la pantalla.
 *
 * El negocio NO tiene moneda configurada (ni en `Settings` ni en el `Product`), así que aquí no
 * se inventa un símbolo: los precios se pintan como números en formato es-CO (miles con punto) y
 * cada columna dice qué son ("precio unitario"). El bot habla del dinero en la conversación; la
 * pantalla solo comprueba que los números sean los correctos.
 */

const LOCALE = 'es-CO';

/** "25.000" / "22.500,5" (es-CO). */
export function formatNumber(value: number): string {
  return value.toLocaleString(LOCALE, { maximumFractionDigits: 2 });
}

/** "Desde 1 unidad" / "Desde 6 unidades". */
export function formatTierQuantity(cantidad: number): string {
  return `Desde ${formatNumber(cantidad)} ${cantidad === 1 ? 'unidad' : 'unidades'}`;
}

/** "6+ · 22.000" — resumen corto de un rango, para las celdas y los chips. */
export function formatTierShort(tier: PriceTier): string {
  return `${formatNumber(tier.cantidad)}+ · ${formatNumber(tier.precio)}`;
}

/**
 * Tabla de precios tal como debe leerse, ordenada por cantidad.
 *
 * El backend la guarda como JSON y no garantiza el orden (el bot la lee en el orden guardado),
 * así que la pantalla la ordena siempre igual.
 */
export function sortedPriceTiers(value: unknown): PriceTier[] {
  if (!Array.isArray(value)) return [];

  const tiers: PriceTier[] = [];

  for (const raw of value) {
    if (typeof raw !== 'object' || raw === null) continue;
    const tier = raw as { cantidad?: unknown; precio?: unknown };
    if (typeof tier.cantidad === 'number' && typeof tier.precio === 'number') {
      tiers.push({ cantidad: tier.cantidad, precio: tier.precio });
    }
  }

  return tiers.sort((a, b) => a.cantidad - b.cantidad);
}

/** Inventario: `null` = sin control ("—"), 0 = agotado, y si no el número de unidades. */
export function inventoryLabel(inventario: number | null): string {
  return inventario === null ? 'Sin control' : formatNumber(inventario);
}

/** ¿Hay que avisar de que está agotado? (el bot no debe seguir vendiéndolo). */
export function isSoldOut(inventario: number | null): boolean {
  return inventario === 0;
}

/**
 * Inventario por combinación leído de forma tolerante (mismo criterio que `priceTierRowsFromJson`).
 */
export function variantStockRows(value: unknown): Array<{ etiqueta: string; cantidad: number }> {
  if (!Array.isArray(value)) return [];

  const filas: Array<{ etiqueta: string; cantidad: number }> = [];

  for (const cruda of value) {
    if (typeof cruda !== 'object' || cruda === null) continue;

    const fila = cruda as { variantes?: unknown; cantidad?: unknown };
    const cantidad = typeof fila.cantidad === 'number' ? fila.cantidad : null;
    if (cantidad === null) continue;

    const variantes =
      typeof fila.variantes === 'object' && fila.variantes !== null && !Array.isArray(fila.variantes)
        ? (fila.variantes as Record<string, unknown>)
        : {};
    const valores = Object.entries(variantes)
      .map(([, valor]) => String(valor ?? '').trim())
      .filter((valor) => valor.length > 0);

    filas.push({ etiqueta: valores.join(' · ') || 'Combinación', cantidad });
  }

  return filas;
}

/**
 * Fotos por combinación leídas de forma tolerante (21-sep-2026), para la ficha del producto: una fila
 * por grupo con su etiqueta («Rojo») y sus URLs. Mismo criterio que `variantStockRows`; los grupos sin
 * fotos no se devuelven.
 */
export function variantPhotoRows(value: unknown): Array<{ etiqueta: string; fotos: string[] }> {
  if (!Array.isArray(value)) return [];

  const filas: Array<{ etiqueta: string; fotos: string[] }> = [];

  for (const cruda of value) {
    if (typeof cruda !== 'object' || cruda === null) continue;

    const fila = cruda as { variantes?: unknown; fotos?: unknown };
    const fotos = Array.isArray(fila.fotos)
      ? fila.fotos.map((foto) => String(foto ?? '').trim()).filter((foto) => foto.length > 0)
      : [];
    if (fotos.length === 0) continue;

    const variantes =
      typeof fila.variantes === 'object' && fila.variantes !== null && !Array.isArray(fila.variantes)
        ? (fila.variantes as Record<string, unknown>)
        : {};
    const valores = Object.entries(variantes)
      .map(([, valor]) => String(valor ?? '').trim())
      .filter((valor) => valor.length > 0);

    filas.push({ etiqueta: valores.join(' · ') || 'Combinación', fotos });
  }

  return filas;
}

/**
 * Cómo se muestra el inventario de un producto en la tabla y en la ficha (21-sep-2026).
 *
 * Con inventario por combinación, un solo número mentiría: se muestra el TOTAL de unidades y se marca
 * agotado cuando TODAS las combinaciones con cantidad están en 0. `detalle` es la lista de combinaciones
 * para el tooltip/ficha.
 */
export function inventorySummary(product: {
  inventario: number | null;
  inventarioVariantes?: unknown;
}): { label: string; soldOut: boolean; hint: string; detalle: Array<{ etiqueta: string; cantidad: number }> } {
  const detalle = variantStockRows(product.inventarioVariantes);

  if (detalle.length === 0) {
    return {
      label: inventoryLabel(product.inventario),
      soldOut: isSoldOut(product.inventario),
      hint: 'Inventario general del producto',
      detalle: [],
    };
  }

  const total = detalle.reduce((suma, fila) => suma + fila.cantidad, 0);
  const hint = detalle.map((fila) => `${fila.etiqueta}: ${formatNumber(fila.cantidad)}`).join(' · ');

  return {
    label: `${formatNumber(total)} por variante`,
    soldOut: detalle.every((fila) => fila.cantidad === 0),
    hint,
    detalle,
  };
}

/** "3 fotos" / "1 regla" (singular y plural explícitos: no hay pluralización automática en es). */
export function supportedProductLabel(count: number, singular: string, plural: string): string {
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`;
}

/**
 * Todo lo que el bot recibe del producto, contado: "3 variantes · 2 reglas · 4 fotos".
 *
 * Es la forma rápida de ver desde la tabla qué productos están "armados" y cuáles no (un
 * producto sin fotos, sin variantes y sin reglas solo aporta nombre y precio al prompt). Solo se
 * listan los grupos que tienen algo: nunca sale un "0 variantes".
 */
export function summarizeProductExtras(product: {
  fotos?: unknown;
  fotosVariantes?: unknown;
  datosParaOrden?: unknown;
  reglas?: unknown;
  faqs?: unknown;
}): string[] {
  const items: string[] = [];

  // Las fotos por variante también las puede mandar el bot, así que cuentan como fotos del producto.
  const generales = Array.isArray(product.fotos) ? product.fotos.length : 0;
  const porVariante = variantPhotoRows(product.fotosVariantes).reduce(
    (total, grupo) => total + grupo.fotos.length,
    0,
  );
  const fotos = generales + porVariante;
  const variantes = Array.isArray(product.datosParaOrden) ? product.datosParaOrden.length : 0;
  const reglas = Array.isArray(product.reglas) ? product.reglas.length : 0;
  const faqs = Array.isArray(product.faqs) ? product.faqs.length : 0;

  if (fotos > 0) items.push(supportedProductLabel(fotos, 'foto', 'fotos'));
  if (variantes > 0) items.push(supportedProductLabel(variantes, 'variante', 'variantes'));
  if (reglas > 0) items.push(supportedProductLabel(reglas, 'regla', 'reglas'));
  if (faqs > 0) items.push(supportedProductLabel(faqs, 'pregunta frecuente', 'preguntas frecuentes'));

  return items;
}

/**
 * Cuántos productos hay y cuántos están activos: "4 productos · 3 activos".
 *
 * Solo los activos llegan al prompt del bot, así que el número de activos es el que importa.
 */
export function summarizeProducts(products: Product[]): string {
  if (products.length === 0) return 'Sin productos';

  const activos = products.filter((product) => product.activo).length;
  return `${formatNumber(products.length)} ${
    products.length === 1 ? 'producto' : 'productos'
  } · ${formatNumber(activos)} ${activos === 1 ? 'activo' : 'activos'}`;
}

/**
 * Rango de la página del catálogo: «Mostrando 1–25 de 120 productos». Mismo formato que el CRM y el
 * Inbox, con el caso de la página que quedó vacía porque el catálogo cambió de tamaño.
 */
export function describeProductRange(offset: number, count: number, total: number): string {
  if (total === 0) return 'Sin productos';
  if (count === 0) return 'Ningún producto en esta página';

  const desde = offset + 1;
  const hasta = offset + count;
  return `Mostrando ${formatNumber(desde)}–${formatNumber(hasta)} de ${formatNumber(total)} ${
    total === 1 ? 'producto' : 'productos'
  }`;
}

/** Cuántos productos por página ofrece la tabla (el valor viaja como `limite` al servidor). */
export const PRODUCT_PAGE_SIZES = [25, 50, 100] as const;
/** Página por defecto del catálogo (coincide con `PRODUCTOS_POR_PAGINA` del backend). */
export const PRODUCT_PAGE_SIZE_DEFAULT = 25;

/** Texto de la lista de variantes: "Color: Negro, Blanco". */
export function describeOrderVariant(variant: OrderVariant): string {
  const opciones = Array.isArray(variant.opciones)
    ? variant.opciones.filter((opcion) => opcion.trim().length > 0)
    : [];

  return opciones.length > 0 ? `${variant.nombre}: ${opciones.join(', ')}` : variant.nombre;
}

/** Texto de una FAQ para los tooltips/aria-labels ("pregunta → respuesta"). */
export function describeFaq(faq: ProductFaq): string {
  return `${faq.pregunta} → ${faq.respuesta}`;
}

/**
 * Nombre del archivo de una foto, para la etiqueta de la miniatura: de una URL con query
 * (`https://x.com/foto.png?v=2`) o de una ruta del storage local (`/uploads/productos/<id>/<uuid>.jpg`).
 *
 * Es solo texto de pantalla: nunca se usa para resolver la imagen (eso lo hace `publicUrl` de
 * `lib/env.ts`); si no hay nada que enseñar, devuelve la propia dirección.
 */
export function photoLabel(url: string): string {
  const clean = url.trim().split('?')[0]?.split('#')[0] ?? '';
  const segments = clean.split('/').filter((segment) => segment.length > 0);
  return segments[segments.length - 1] ?? url.trim();
}

/**
 * Valores únicos (sin vacíos) de `marca` o `categoria`, ordenados alfabéticamente en español.
 *
 * Alimenta los desplegables de filtro de la tabla: el catálogo entero ya está en caché, así que las
 * opciones se sacan de los propios productos (sin endpoint nuevo ni lista que mantener aparte).
 */
export function uniqueProductValues(
  products: Array<{ marca?: string | null; categoria?: string | null }>,
  campo: 'marca' | 'categoria',
): string[] {
  const valores = new Set<string>();

  for (const product of products) {
    const valor = (product[campo] ?? '').trim();
    if (valor) valores.add(valor);
  }

  return [...valores].sort((a, b) => a.localeCompare(b, 'es'));
}
